import React, { useEffect, useRef } from 'react';
import './Snowfall.scss';

const Snowfall = () => {
    const canvasRef = useRef(null);
    const particlesRef = useRef([]);
    const windForceRef = useRef(0);
    const animationFrameRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let width, height;

        // Настройки снегопада
        const settings = {
            minSize: 1,
            maxSize: 4,
            minSpeed: 0.5,
            maxSpeed: 2.0,
            windSpeed: 0.5,
            density: 12000
        };

        const windDecay = 0.95;

        // Класс снежинки
        class Snowflake {
            constructor() {
                this.reset(true);
            }

            reset(initial = false) {
                this.x = Math.random() * width;
                this.y = initial ? Math.random() * height : -10;
                this.size = Math.random() * (settings.maxSize - settings.minSize) + settings.minSize;
                this.speedY = (this.size / settings.maxSize) * (settings.maxSpeed - settings.minSpeed) + settings.minSpeed;
                this.angle = Math.random() * Math.PI * 2;
                this.spin = Math.random() * 0.05 + 0.01;
                this.opacity = (this.size / settings.maxSize);
            }

            update() {
                this.y += this.speedY;
                this.angle += this.spin;
                this.x += Math.sin(this.angle) * settings.windSpeed + windForceRef.current;

                if (this.y > height + 10 || this.x > width + 10 || this.x < -10) {
                    if (this.y > height + 10) {
                        this.reset();
                    } else {
                        if (this.x > width + 10) this.x = -10;
                        if (this.x < -10) this.x = width + 10;
                    }
                }
            }

            draw() {
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
                ctx.fill();
            }
        }

        // Изменение размеров канваса
        const resize = () => {
            width = window.innerWidth;
            height = window.innerHeight;

            // Для мобильных устройств ограничиваем максимальную ширину
            const maxWidth = 480;
            if (width > maxWidth) {
                width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            const area = width * height;
            const targetCount = Math.min(Math.max(Math.floor(area / settings.density), 30), 250);

            if (particlesRef.current.length < targetCount) {
                while (particlesRef.current.length < targetCount) {
                    particlesRef.current.push(new Snowflake());
                }
            } else if (particlesRef.current.length > targetCount) {
                particlesRef.current.splice(targetCount);
            }
        };

        // Главный цикл анимации
        const animate = () => {
            ctx.clearRect(0, 0, width, height);

            for (let flake of particlesRef.current) {
                flake.update();
                flake.draw();
            }

            if (Math.abs(windForceRef.current) > 0.01) {
                windForceRef.current *= windDecay;
            } else {
                windForceRef.current = 0;
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Обработка движения мыши
        const handleMouseMove = (e) => {
            windForceRef.current += (e.movementX * 0.05);
            if (windForceRef.current > 3) windForceRef.current = 3;
            if (windForceRef.current < -3) windForceRef.current = -3;
        };

        // Обработка касаний
        const handleTouchMove = (e) => {
            const touch = e.touches[0];
            const center = width / 2;
            const direction = (touch.clientX - center) / center;
            windForceRef.current = direction * 2;
        };

        // Инициализация
        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('touchmove', handleTouchMove, { passive: true });

        resize();
        animate();

        // Очистка при размонтировании
        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchmove', handleTouchMove);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            particlesRef.current = [];
        };
    }, []);

    return (
        <div className="snowfall-container">
            <canvas ref={canvasRef} className="snowfall-canvas" />
        </div>
    );
};

export default Snowfall;
